import { NavLink, useLocation } from 'react-router-dom';
import { Bell, Home, LayoutGrid } from 'lucide-react';
import type { NavItem } from './nav';
import { primaryTabs } from './mobile-nav';

/**
 * شريطُ التبويب السفلي على الجوال (25 سبتمبر 2026) — ما يجعل المنصة **تطبيقاً لا موقعاً**.
 *
 * كانت القائمةُ على الجوال زرّاً في الأعلى يفتح لوحاً جانبياً بثلاثين بنداً: الإبهامُ لا يصل
 * إلى أعلى الشاشة، وكلُّ انتقالٍ ثلاثُ لمسات. فصار في الأسفل خمسةُ أبواب:
 *   · «الرئيسية» — صفحةُ الدور الأولى (`homePathFor`).
 *   · بابان أساسيّان **بحسب الدور** من قائمة أولويةٍ ثابتة — وليُّ الأمر «أبنائي»، والأخصائي
 *     «طلبتي» و«الجلسات»، والمعلّم «صفّي»… — مأخوذان من البنود التي يراها أصلاً، فلا يظهر
 *     تبويبٌ لشاشةٍ لا يملكها.
 *   · «التنبيهات» بعدّاد غير المقروء.
 *   · «المزيد» يفتح القائمة الكاملة نفسَها — لا قائمةٌ ثانية تتخلّف عن الأولى.
 */

const tabClass = ({ isActive }: { isActive: boolean }) => `tabbar__item${isActive ? ' is-current' : ''}`;

export function MobileTabBar({ items, homePath, unread, onMore, moreOpen }: {
  items: NavItem[]; homePath: string; unread: number; onMore: () => void; moreOpen: boolean;
}) {
  const { pathname } = useLocation();
  const primary = primaryTabs(items, homePath);
  const hasInbox = items.some((i) => i.to === '/me/notifications');
  // «الرئيسية» حاليّةٌ في صفحتها وحدها — لا في كل ما يبدأ بمسارها (/app/plans ليست الرئيسية)
  const homeActive = pathname === homePath;

  return (
    <nav className="tabbar" aria-label="التنقّل السريع">
      <NavLink to={homePath} className={() => tabClass({ isActive: homeActive })} end>
        <Home aria-hidden /><span>الرئيسية</span>
      </NavLink>
      {primary.map(({ to, label, item }) => {
        const Icon = item.icon;
        return (
          <NavLink key={to} to={to} className={tabClass} data-tour={`nav:${to}`}>
            <Icon aria-hidden /><span>{label}</span>
          </NavLink>
        );
      })}
      {hasInbox && (
        <NavLink to="/me/notifications" className={tabClass} data-tour="nav:/me/notifications" aria-label={unread ? `التنبيهات — ${unread} غير مقروء` : 'التنبيهات'}>
          <span className="tabbar__icon">
            <Bell aria-hidden />
            {unread > 0 && <b className="tabbar__badge" aria-hidden>{unread > 99 ? '99+' : unread}</b>}
          </span>
          <span>التنبيهات</span>
        </NavLink>
      )}
      <button type="button" className={`tabbar__item${moreOpen ? ' is-current' : ''}`} onClick={onMore} aria-expanded={moreOpen} data-tour="nav">
        <LayoutGrid aria-hidden /><span>المزيد</span>
      </button>
    </nav>
  );
}
